import * as UI from "@iyk/ui"
import { Analytics, useAnalytics } from "@iyk/vf-observability/react"
import * as React from "react"

import { appConfig } from "../../app-config.ts"
import { GlobalRoutes } from "../../react-router/routes.ts"
import { Link } from "../../ui/link.tsx"
import { Profile } from "../profile.ts"

export const LockedExclusiveCard = ({ lockedExclusive }: LockedExclusiveCard.Props) => {
  const [openDrawer, setOpenDrawer] = React.useState(false)
  const toggleDrawer = () => setOpenDrawer((prev) => !prev)

  const trigger = Profile.LockedExclusive.$match(lockedExclusive, {
    Video: ({ title, coverImage }) => (
      <MediaTrigger title={title} thumb={coverImage} onClick={toggleDrawer} />
    ),
    Gallery: ({ title, coverImage }) => (
      <MediaTrigger title={title} thumb={coverImage} onClick={toggleDrawer} />
    ),
    Sweepstake: ({ title, coverImage }) => (
      <MediaTrigger title={title} thumb={coverImage} onClick={toggleDrawer} />
    ),
    Playlist: ({ playlist }) => (
      <PlaylistTrigger
        title={playlist.title}
        artist={playlist.artist}
        thumb={playlist.coverImageUrl}
        tracks={playlist.trackCount}
        onClick={toggleDrawer}
      />
    )
  })

  return (
    <UI.Drawer.Root open={openDrawer} onOpenChange={setOpenDrawer}>
      {trigger}
      <DrawerContent
        id={lockedExclusive.id}
        title={lockedExclusive.title}
        thumb={lockedExclusive.coverImage}
        requirements={lockedExclusive.requirements}
      />
    </UI.Drawer.Root>
  )
}

const MediaTrigger = ({ title, thumb, onClick }: LockedExclusiveCard.TriggerProps) => (
  <div
    role="button"
    onClick={onClick}
    className="relative aspect-video w-full rounded overflow-hidden select-none"
  >
    <img src={thumb} alt={title} className="absolute inset-0 size-full object-cover blur-[3px]" />
    <div className="absolute inset-0 bg-black/60" />
    <div className="absolute bottom-0 left-0 w-full h-1/4 bg-[linear-gradient(to_top,_black,_rgba(0,0,0,0))]" />

    <div className="absolute inset-0 flex items-center justify-center">
      <UI.CircleButton type="button" className="size-10">
        <UI.Icon.Lock />
      </UI.CircleButton>
    </div>

    <div className="absolute left-2 bottom-2 text-xs">{title}</div>
    <UnlockHint />
  </div>
)

const PlaylistTrigger = ({
  title,
  artist,
  thumb,
  tracks,
  onClick
}: LockedExclusiveCard.PlaylistTriggerProps) => (
  <div
    role="button"
    onClick={onClick}
    className="flex h-36 overflow-hidden rounded bg-grayA-2 relative select-none"
  >
    <div className="aspect-square relative">
      <img src={thumb} alt={title} className="size-full object-cover" />
      <div className="absolute inset-0 bg-black/70" />
      <div className="absolute inset-0 flex items-center justify-center">
        <UI.CircleButton type="button" className="size-8">
          <UI.Icon.Lock />
        </UI.CircleButton>
      </div>
    </div>

    <div className="flex-1 flex flex-col justify-between px-3 py-2 text-left">
      <div>
        <p className="line-clamp-2">{title}</p>
        <p className="text-sm text-gray-11">{artist}</p>
      </div>
      <p className="text-sm text-gray-11">
        {tracks} {tracks > 1 ? "canciones" : "canción"}
      </p>
    </div>

    <UnlockHint />
  </div>
)

const UnlockHint = () => (
  <div className="absolute right-2 bottom-2">
    <span className="bg-gray-12 px-1 py-0.5 rounded-sm text-xs text-gray-1 flex items-center gap-1">
      Cómo desbloquear <UI.Icon.Question />
    </span>
  </div>
)

const DrawerContent = ({ id, requirements }: LockedExclusiveCard.DrawerContentProps) => {
  const analytics = useAnalytics()
  const shopUrl = appConfig.shopUrl

  return (
    <UI.Drawer.Portal>
      <UI.Drawer.Overlay />
      <UI.Drawer.Content className="mx-auto w-full max-w-[--app-max-width] bg-gray-3">
        <div className="grid px-4 pb-6">
          <UI.Drawer.Header>
            <UI.Drawer.Title className="text-xs text-gray-11">Este contenido está bloqueado</UI.Drawer.Title>
            <UI.Drawer.Description className="text-sm">
              Recoge uno de las siguientes
              <br />
              pruebas para acceder
            </UI.Drawer.Description>
          </UI.Drawer.Header>

          <div className="grid gap-1.5 p-1.5 bg-gray-2">
            {(() => {
              const hasJoinProof = requirements.some((r) => r._tag === "JoinProof")
              const hasMerchSku = requirements.some((r) => r._tag === "MerchSku")

              if (hasJoinProof) {
                return (
                  <Link
                    to={GlobalRoutes.join.landing}
                    className={UI.classForButton({ kind: "solid", size: "md" })}
                  >
                    Convertirme en fan y desbloquear contenido
                  </Link>
                )
              } else if (hasMerchSku && shopUrl) {
                return (
                  <a
                    href={shopUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={UI.classForButton({ kind: "solid", size: "md" })}
                  >
                    Tienda <UI.Icon.ArrowUpRight />
                  </a>
                )
              }
              return null
            })()}

            {requirements.map((r, idx) => {
              const content = (
                <div
                  className={UI.cx(
                    "flex items-center gap-3",
                    "[&:not(:last-child)]:pb-1.5",
                    "[&:not(:last-child)]:bshadow-b [&:not(:last-child)]:bshadow-gray-6"
                  )}
                >
                  <img src={r.imageSrc} alt={r.name} className="size-7 object-contain rounded-sm bg-gray-4" />
                  <span className="text-xs text-gray-11">{r.name}</span>
                  {Profile.Requirement.$match(r, {
                    MerchSku: () => <UI.Icon.ArrowRight className="ml-auto text-gray-10" />,
                    JoinProof: () => <UI.Icon.ArrowRight className="ml-auto text-gray-10" />,
                    Show: () => null,
                    DigitalProof: () => null
                  })}
                </div>
              )

              return Profile.Requirement.$match(r, {
                MerchSku: ({ id }) => (
                  <Link
                    key={idx}
                    to={`/profile/merch/${id}` as any}
                    onClick={() => analytics.track(Analytics.Event.MerchBuyNowClicked({ merchSkuId: id }))}
                  >
                    {content}
                  </Link>
                ),
                Show: () => <div key={idx}>{content}</div>,
                JoinProof: () => (
                  <Link key={idx} to={GlobalRoutes.join.landing}>
                    {content}
                  </Link>
                ),
                DigitalProof: () => <div key={idx}>{content}</div>
              })
            })}
          </div>
        </div>
      </UI.Drawer.Content>
    </UI.Drawer.Portal>
  )
}

namespace LockedExclusiveCard {
  export type Props = {
    lockedExclusive: Profile.LockedExclusive
  }

  export type TriggerProps = {
    title: string
    thumb: string
    onClick: () => void
  }

  export type PlaylistTriggerProps = TriggerProps & {
    artist: string
    tracks: number
  }

  export type DrawerContentProps = {
    id: string
    title: string
    thumb: string
    requirements: Array<Profile.Requirement>
  }
}
